import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { invocarFuncion } from '../lib/invocarFuncion'
import { useWorkspace } from '../lib/WorkspaceContext'
import './Reportes.css'

const TIPOS = ['bug', 'sugerencia', 'consulta', 'incidencia']
const TIPO_LABELS = { bug: 'Bug', sugerencia: 'Sugerencia', consulta: 'Consulta', incidencia: 'Incidencia' }
const ESTADOS = ['nuevo', 'en_revision', 'resuelto', 'descartado']
const ESTADO_LABELS = { nuevo: 'Nuevo', en_revision: 'En revisión', resuelto: 'Resuelto', descartado: 'Descartado' }
const PRIORIDADES = ['alta', 'media', 'baja']
const PRIORIDAD_LABELS = { alta: 'Alta', media: 'Media', baja: 'Baja' }

export default function Reportes({ usuario, onVolver }) {
  const { workspaceActivo } = useWorkspace()
  return <FormularioReporte usuario={usuario} workspaceActivo={workspaceActivo} onVolver={onVolver} />
}

function FormularioReporte({ usuario, workspaceActivo, onVolver }) {
  const esOwner = ['owner', 'staff'].includes(workspaceActivo?.rolEnWorkspace)
  const [tipo, setTipo] = useState(esOwner ? 'incidencia' : 'bug')
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [misReportes, setMisReportes] = useState([])
  const [loadingLista, setLoadingLista] = useState(true)

  useEffect(() => {
    cargarMisReportes()
  }, [])

  const cargarMisReportes = async () => {
    setLoadingLista(true)
    const { data, error } = await supabase
      .from('reportes')
      .select('*')
      .eq('usuario_id', usuario.id)
      .order('created_at', { ascending: false })
    if (error) console.error('Error al cargar mis reportes:', error)
    setMisReportes(data ?? [])
    setLoadingLista(false)
  }

  const enviar = async () => {
    if (!titulo.trim() || !descripcion.trim() || !workspaceActivo) return
    setEnviando(true)
    const prioridad = esOwner ? 'alta' : 'media'
    const { data, error } = await supabase
      .from('reportes')
      .insert({
        workspace_id: workspaceActivo.id,
        usuario_id: usuario.id,
        rol_reportante: esOwner ? 'owner' : 'paciente',
        tipo,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        prioridad
      })
      .select()
      .single()

    if (error) {
      console.error('Error al enviar el reporte:', error)
      setEnviando(false)
      return
    }

    if (prioridad === 'alta') {
      const { error: errorNotif } = await invocarFuncion('notificar-reporte-alta', {
        body: { reporte_id: data.id }
      })
      if (errorNotif) {
        console.error('El reporte se guardó, pero no se pudo disparar la notificación de prioridad alta:', errorNotif)
      }
    }

    setTitulo('')
    setDescripcion('')
    setEnviando(false)
    setEnviado(true)
    cargarMisReportes()
  }

  return (
    <div className="reportes-page">
      <div className="page-header">
        {onVolver && <button className="clases-back" onClick={onVolver}>← Perfil</button>}
        <h1 className="page-titulo">{esOwner ? 'Reportar una incidencia' : 'Contanos qué te pasó'}</h1>
      </div>

      <div className="reportes-contenido">
        {enviado ? (
          <div className="reportes-card reportes-confirmacion">
            <p>
              {esOwner
                ? 'Recibimos tu reporte y lo estamos priorizando. Te vamos a contactar en cuanto tengamos una solución o necesitemos más información.'
                : '¡Gracias por tu mensaje! Valoramos muchísimo tu ayuda — construir un producto que realmente sirva a nuestra comunidad depende de escuchar a quienes forman parte de ella. 💛'}
            </p>
            <button className="btn-secundario" onClick={() => setEnviado(false)}>Enviar otro</button>
          </div>
        ) : (
          <div className="reportes-card">
            <div className="reportes-tipo-btns">
              {TIPOS.map(t => (
                <button
                  key={t}
                  className={`clases-tipo-btn ${tipo === t ? 'active' : ''}`}
                  onClick={() => setTipo(t)}
                >{TIPO_LABELS[t]}</button>
              ))}
            </div>
            <input
              className="form-input"
              placeholder="Título breve"
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
            />
            <textarea
              className="form-input reportes-textarea"
              placeholder={esOwner
                ? 'Describí qué estaba pasando: qué pantalla o función falló, en qué momento, y si podés, los pasos para reproducirlo.'
                : 'Contanos qué pantalla o función no te funcionó, o qué te gustaría que sumemos. Cuanto más detalle nos des, mejor podemos ayudarte.'}
              rows={6}
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
            />
            <button className="btn-primario" onClick={enviar} disabled={enviando || !titulo.trim() || !descripcion.trim()}>
              {enviando ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        )}

        <div className="reportes-card">
          <p className="reportes-seccion-titulo">Mis reportes</p>
          {loadingLista ? (
            <p className="reportes-vacio">Cargando...</p>
          ) : misReportes.length === 0 ? (
            <p className="reportes-vacio">Todavía no enviaste ningún reporte.</p>
          ) : (
            <div className="reportes-lista">
              {misReportes.map(r => (
                <div key={r.id} className="reportes-item">
                  <div className="reportes-item-header">
                    <p className="reportes-item-titulo">{r.titulo}</p>
                    <span className={`reportes-badge reportes-badge-${r.estado}`}>{ESTADO_LABELS[r.estado]}</span>
                  </div>
                  <p className="reportes-item-desc">{r.descripcion}</p>
                  <p className="reportes-item-meta">{TIPO_LABELS[r.tipo]} · {new Date(r.created_at).toLocaleDateString('es-AR')}</p>
                  {r.respuesta && (
                    <div className="reportes-item-respuesta">
                      <p className="reportes-item-respuesta-titulo">Respuesta del equipo</p>
                      <p className="reportes-item-desc">{r.respuesta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function BandejaReportes({ onVolver }) {
  const [reportes, setReportes] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroPrioridad, setFiltroPrioridad] = useState('todas')
  const [filtroRol, setFiltroRol] = useState('todos')
  const [filtroWorkspace, setFiltroWorkspace] = useState('todos')
  const [reporteAbierto, setReporteAbierto] = useState(null)
  const [textoRespuesta, setTextoRespuesta] = useState('')
  const [guardandoRespuesta, setGuardandoRespuesta] = useState(false)

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setLoading(true)
    // public.usuarios solo deja leer la fila propia (RLS) — el nombre del
    // autor no se puede resolver con un select directo, hace falta la RPC
    // SECURITY DEFINER (mismo patrón que last_sign_in_at en Panel Workspaces).
    const { data, error } = await supabase.rpc('listar_reportes_super_admin')
    if (error) console.error('Error al cargar la bandeja de reportes:', error)
    setReportes(data ?? [])
    setLoading(false)
  }

  const actualizarCampo = async (id, campo, valor) => {
    const { error } = await supabase.from('reportes').update({ [campo]: valor, updated_at: new Date().toISOString() }).eq('id', id)
    if (error) { console.error(`Error al actualizar ${campo} del reporte ${id}:`, error); return }
    setReportes(prev => prev.map(r => r.id === id ? { ...r, [campo]: valor } : r))
  }

  const abrirDetalle = (r) => {
    setReporteAbierto(r)
    setTextoRespuesta(r.respuesta || '')
  }

  const guardarRespuesta = async () => {
    if (!reporteAbierto) return
    setGuardandoRespuesta(true)
    const ahora = new Date().toISOString()
    const { error } = await supabase
      .from('reportes')
      .update({ respuesta: textoRespuesta.trim() || null, respondido_en: ahora, updated_at: ahora })
      .eq('id', reporteAbierto.id)
    setGuardandoRespuesta(false)
    if (error) { console.error('Error al guardar la respuesta:', error); return }
    setReportes(prev => prev.map(r => r.id === reporteAbierto.id ? { ...r, respuesta: textoRespuesta.trim() || null, respondido_en: ahora } : r))
    setReporteAbierto(prev => prev ? { ...prev, respuesta: textoRespuesta.trim() || null, respondido_en: ahora } : prev)
  }

  const workspacesDisponibles = [...new Map(reportes.map(r => [r.workspace_id, r.workspace_nombre])).entries()]

  const reportesFiltrados = reportes.filter(r =>
    (filtroEstado === 'todos' || r.estado === filtroEstado) &&
    (filtroPrioridad === 'todas' || r.prioridad === filtroPrioridad) &&
    (filtroRol === 'todos' || r.rol_reportante === filtroRol) &&
    (filtroWorkspace === 'todos' || r.workspace_id === filtroWorkspace)
  )

  if (loading) return <div className="loading">Cargando reportes...</div>

  return (
    <div className="reportes-page">
      <div className="page-header">
        {onVolver && <button className="clases-back" onClick={onVolver}>← Perfil</button>}
        <h1 className="page-titulo">Bandeja de reportes</h1>
        <p className="page-subtitulo">{reportesFiltrados.length} de {reportes.length} reportes</p>
      </div>

      <div className="reportes-contenido">
        <div className="reportes-filtros">
          <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
            <option value="todos">Todos los estados</option>
            {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABELS[e]}</option>)}
          </select>
          <select className="form-input" value={filtroPrioridad} onChange={e => setFiltroPrioridad(e.target.value)}>
            <option value="todas">Todas las prioridades</option>
            {PRIORIDADES.map(p => <option key={p} value={p}>{PRIORIDAD_LABELS[p]}</option>)}
          </select>
          <select className="form-input" value={filtroRol} onChange={e => setFiltroRol(e.target.value)}>
            <option value="todos">Paciente y nutri</option>
            <option value="paciente">Paciente</option>
            <option value="owner">Nutri</option>
          </select>
          <select className="form-input" value={filtroWorkspace} onChange={e => setFiltroWorkspace(e.target.value)}>
            <option value="todos">Todos los workspaces</option>
            {workspacesDisponibles.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
        </div>

        {reportesFiltrados.length === 0 ? (
          <p className="reportes-vacio">No hay reportes con estos filtros.</p>
        ) : (
          <div className="reportes-tabla-wrap">
            <table className="reportes-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Autor</th>
                  <th>Workspace</th>
                  <th>Asunto</th>
                  <th>Prioridad</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {reportesFiltrados.map(r => (
                  <tr key={r.id} className="reportes-tabla-fila-clickable" onClick={() => abrirDetalle(r)}>
                    <td>{new Date(r.created_at).toLocaleDateString('es-AR')}</td>
                    <td>
                      {r.autor_nombre || r.autor_email}
                      <span className="reportes-tabla-rol"> · {r.rol_reportante === 'owner' ? 'Nutri' : 'Paciente'}</span>
                    </td>
                    <td>{r.workspace_nombre}</td>
                    <td className="reportes-tabla-asunto">
                      <span className="reportes-badge">{TIPO_LABELS[r.tipo]}</span> {r.titulo}
                      {r.respuesta && <span className="reportes-tabla-respondido" title="Ya tiene respuesta">✓</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <select className="form-input" style={{ padding: '6px 8px', fontSize: 12 }} value={r.prioridad} onChange={e => actualizarCampo(r.id, 'prioridad', e.target.value)}>
                        {PRIORIDADES.map(p => <option key={p} value={p}>{PRIORIDAD_LABELS[p]}</option>)}
                      </select>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <select className="form-input" style={{ padding: '6px 8px', fontSize: 12 }} value={r.estado} onChange={e => actualizarCampo(r.id, 'estado', e.target.value)}>
                        {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABELS[e]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reporteAbierto && (
        <div className="modal-overlay" onClick={() => setReporteAbierto(null)}>
          <div className="modal-card modal-card-scroll" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">{reporteAbierto.titulo}</h3>
            <p className="reportes-item-meta">
              {reporteAbierto.workspace_nombre} · {reporteAbierto.autor_nombre || reporteAbierto.autor_email} ({reporteAbierto.rol_reportante === 'owner' ? 'Nutri' : 'Paciente'}) · {TIPO_LABELS[reporteAbierto.tipo]} · {new Date(reporteAbierto.created_at).toLocaleDateString('es-AR')}
            </p>
            <p className="reportes-item-desc" style={{ marginTop: 12 }}>{reporteAbierto.descripcion}</p>

            <p className="reportes-seccion-titulo" style={{ marginTop: 16 }}>Respuesta</p>
            <textarea
              className="form-input reportes-textarea"
              placeholder="Escribí una respuesta para quien reportó esto — la va a ver en 'Mis reportes'."
              rows={5}
              value={textoRespuesta}
              onChange={e => setTextoRespuesta(e.target.value)}
            />
            {reporteAbierto.respondido_en && (
              <p className="reportes-item-meta">Última respuesta: {new Date(reporteAbierto.respondido_en).toLocaleDateString('es-AR')}</p>
            )}
            <button className="btn-primario" onClick={guardarRespuesta} disabled={guardandoRespuesta}>
              {guardandoRespuesta ? 'Guardando...' : 'Guardar respuesta'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
